import type { LucideIcon } from 'lucide-react';
import { Shield, Trophy, Rocket, Target, Flag, Sparkles, Award } from 'lucide-react';
import { VISIBLE_CHANNEL_GOALS } from './progress-channel-goals';

// Maturity Index inputs in the pared-down section: completed channel goals
// across the visible channels, and KPI targets that have been set above zero.
// ROADMAP_TASK / AUDIT_PASS / AUDIT_WATCH stay defined so the auto-derived
// Foundation Achievements (which still read the snapshot shape) can reference
// the same constants if they need to weight their thresholds; they no longer
// contribute to the score itself.
export const POINTS = {
  CHANNEL_GOAL: 5,
  KPI_HIT: 15,
  ROADMAP_TASK: 5,
  AUDIT_PASS: 10,
  AUDIT_WATCH: 4,
};

const KPI_COUNT = 4;

export const MAX_XP =
  VISIBLE_CHANNEL_GOALS.length * POINTS.CHANNEL_GOAL + KPI_COUNT * POINTS.KPI_HIT;

export interface LevelTier {
  level: number;
  min: number;
  title: string;
}

// Thresholds rebalanced for the new MAX_XP (~320). Five stages spaced so that
// each one demands a meaningful amount of channel-goal progress before it
// unlocks, with the top stage requiring near-complete coverage.
export const LEVELS: LevelTier[] = [
  { level: 1, min: 0, title: 'Setup' },
  { level: 2, min: 60, title: 'Optimising' },
  { level: 3, min: 130, title: 'Scaling' },
  { level: 4, min: 210, title: 'Established' },
  { level: 5, min: 290, title: 'Market Leader' },
];

export interface ProgressSnapshot {
  roadmap: {
    foundationDone: number;
    stabilisationDone: number;
    scaleDone: number;
    totalDone: number;
    total: number;
  };
  audit: { pass: number; watch: number; fail: number };
  kpis: { hit: number; total: number };
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  unlocked: (s: ProgressSnapshot) => boolean;
}

export const ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first-step',
    title: 'First Step',
    description: 'Complete any roadmap task',
    icon: Sparkles,
    unlocked: (s) => s.roadmap.totalDone >= 1,
  },
  {
    id: 'foundation-cleared',
    title: 'Foundation Cleared',
    description: 'Finish all 6 Foundation tasks',
    icon: Flag,
    unlocked: (s) => s.roadmap.foundationDone >= 6,
  },
  {
    id: 'stabilisation-set',
    title: 'Stabilisation Set',
    description: 'Finish all 7 Stabilisation tasks',
    icon: Rocket,
    unlocked: (s) => s.roadmap.stabilisationDone >= 7,
  },
  {
    id: 'scale-shipped',
    title: 'Scale Shipped',
    description: 'Finish all 7 Scale tasks',
    icon: Trophy,
    unlocked: (s) => s.roadmap.scaleDone >= 7,
  },
  {
    id: 'audit-clean',
    title: 'Audit Clean Sweep',
    description: '7 / 7 audit dimensions passing',
    icon: Shield,
    unlocked: (s) => s.audit.pass === 7,
  },
  {
    id: 'no-criticals',
    title: 'No Criticals',
    description: '0 audit dimensions failing',
    icon: Award,
    unlocked: (s) => s.audit.fail === 0,
  },
  {
    id: 'half-kpis',
    title: 'On Target',
    description: 'Hit at least 2 of 4 KPI targets',
    icon: Target,
    unlocked: (s) => s.kpis.hit >= 2,
  },
  {
    id: 'all-kpis',
    title: 'KPI Master',
    description: 'Hit all 4 KPI targets',
    icon: Target,
    unlocked: (s) => s.kpis.hit === 4,
  },
];
